import { useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from '../../../../components/avatar';
import { Badge } from '../../../../components/badge';
import MobileProfileMenu from '../MobileProfileMenu';
import type { HeaderNavLinks, HeaderUserData } from '../Header';

interface MobileUserMenuProps {
    userData: HeaderUserData;
    profileLinks: HeaderNavLinks;
    onLogout: () => void;
    currentUrl: string;
    currentCountryCode?: string;
    /**
     * Draw the revamp's avatar: 32px, with the notification count living on
     * the bar's own bell. Off, the avatar is 40px and carries the count
     * itself — the arrangement every visitor sees while `revamp_2026` is off.
     */
    revamp?: boolean;
}

export const MobileUserMenu = (props: MobileUserMenuProps) => {
    const {
        userData,
        profileLinks,
        onLogout,
        currentUrl,
        currentCountryCode,
        revamp = false,
    } = props;
    const {
        avatarSrc = '',
        firstName,
        lastName = '',
        notificationsCount,
    } = userData;

    const [showMenu, setShowMenu] = useState(false);
    const initials = useMemo(() => {
        return `${firstName[0]?.toUpperCase() ?? ''}${lastName[0]?.toUpperCase() ?? ''}`;
    }, [firstName, lastName]);

    useEffect(() => {
        // Close the menu on page navigation.
        setShowMenu(false);
    }, [currentUrl]);

    return (
        <div>
            <div className="relative">
                {/* 32, 80% of the 40 it was: level with the header's other
                    icons rather than standing a size above them. The
                    notification count sits on the bar's own bell, not here. */}
                {/* A real button, not a div wearing `role="button"`: that
                    carried the click and the name but no tab stop and no key
                    handler, so the account was reachable by pointer only. */}
                <button
                    type="button"
                    aria-label="My Profile"
                    aria-expanded={showMenu}
                    // Flex, not the button's default inline-block: an inline
                    // box sits on a line box whose descender space pushed the
                    // avatar above the bar's other icons.
                    className="flex cursor-pointer border-0 bg-transparent p-0"
                    onClick={() => {
                        setShowMenu((state) => !state);
                    }}
                >
                    <Avatar size={revamp ? 'square32' : 'square40'}>
                        <AvatarImage src={avatarSrc} />
                        <AvatarFallback size={revamp ? 'sm' : 'default'}>
                            {initials}
                        </AvatarFallback>
                    </Avatar>
                </button>

                {!revamp && notificationsCount > 0 ? (
                    <div className="absolute -top-1 -right-1 flex">
                        <Badge
                            size="centeredCompact"
                            text={notificationsCount > 9 ? 'small' : 'default'}
                        >
                            {notificationsCount > 9 ? '9+' : notificationsCount}
                        </Badge>
                    </div>
                ) : null}
            </div>
            {showMenu && (
                <MobileProfileMenu
                    userData={userData}
                    profileLinks={profileLinks}
                    setShowProfileMenu={setShowMenu}
                    showProfileMenu={showMenu}
                    onLogout={onLogout}
                    currentCountryCode={currentCountryCode}
                />
            )}
        </div>
    );
};
